// src/pages/VendorDetail.tsx — §28.518.99.47.d
// Rendered inside the Vendors page modal AND inside PropertyDetail's Vendors
// tab. Handles employees + property grants management.
//
// Access rules (spec §Provisioning gates):
//   - dealer/customer/PM  → full CRUD on employees + grants
//   - vendor_admin        → CRUD on employees for own vendor only
//   - vendor              → own record view only (no employee/grant mgmt)

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Table } from '../components/ui/Table';
import { AuthContext } from '../context/AuthContext';
import { Role } from '../types';
import { isAdmin, isDealer, isCustomer, isPropertyManager } from '../utils/roles';
import { apiList, apiPost } from '../api/apiClient';
import { formatDate } from '../utils/formatters';
import {
  Vendor,
  VendorEmployee,
  VendorGrant,
  getVendor,
  updateVendor,
  listVendorEmployees,
  createVendorEmployee,
  activateVendorEmployee,
  listVendorGrants,
  createVendorGrant,
  revokeVendorGrant,
} from '../api/vendors';

interface VendorDetailProps {
  vendorId: string;
  onClose?: () => void;
  onChanged?: (v: Vendor) => void;
  /** Passed by Vendors.tsx when a vendor_admin JWT is viewing their own vendor. */
  asAdmin?: boolean;
  /** When rendered inside PropertyDetail — pins grants to this property. */
  scopedPropertyId?: string;
}

type TabKey = 'overview' | 'employees' | 'grants';

const METHOD_OPTIONS = ['pin', 'card', 'face', 'relay_button', 'mobile_app']; // BLE excluded per spec

export const VendorDetail: React.FC<VendorDetailProps> = ({
  vendorId,
  onChanged,
  asAdmin,
  scopedPropertyId,
}) => {
  const { user } = useContext(AuthContext);
  const role = (user?.role || 'customer') as Role;
  const personType = (user as any)?.person_type as string | undefined;
  const isVendorSelf = personType === 'vendor';
  const isVendorAdmin = personType === 'vendor_admin' || !!asAdmin;

  const canManageVendor =
    isAdmin(role) || isDealer(role) || isCustomer(role) || isPropertyManager(role);
  const canManageEmployees = canManageVendor || isVendorAdmin;
  const canManageGrants = canManageVendor; // vendors can't self-grant properties
  const readonlyView = isVendorSelf;

  const [tab, setTab] = useState<TabKey>('overview');
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [employees, setEmployees] = useState<VendorEmployee[]>([]);
  const [grants, setGrants] = useState<VendorGrant[]>([]);
  const [properties, setProperties] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [v, emps, grs, props] = await Promise.all([
        getVendor(vendorId).catch(() => null),
        listVendorEmployees(vendorId).catch(() => [] as VendorEmployee[]),
        listVendorGrants(vendorId).catch(() => [] as VendorGrant[]),
        apiList('properties').catch(() => [] as any[]),
      ]);
      setVendor(v);
      setEmployees(emps);
      setGrants(grs);
      const pm: Record<string, any> = {};
      (props as any[]).forEach((p: any) => {
        pm[String(p.uuid || p.id)] = p;
      });
      setProperties(pm);
    } catch (e: any) {
      setError(e?.message || 'Failed to load vendor');
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" />
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="p-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded">
        {error || 'Vendor not found'}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{vendor.name}</h2>
          <p className="text-xs text-gray-500">
            {vendor.category
              ? vendor.category.charAt(0).toUpperCase() + vendor.category.slice(1)
              : 'Uncategorized'}
            {vendor.contact_email && <> · {vendor.contact_email}</>}
            {vendor.contact_phone && <> · {vendor.contact_phone}</>}
          </p>
        </div>
        {vendor.revoked_at ? (
          <Badge status="suspended">Revoked</Badge>
        ) : (
          <Badge status="active">Active</Badge>
        )}
      </div>

      {/* Tab strip */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6" aria-label="Tabs">
          {(['overview', 'employees', 'grants'] as TabKey[]).map((t) => {
            if (t === 'grants' && readonlyView) return null;
            return (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`py-2 px-1 text-sm font-medium border-b-2 -mb-px capitalize ${
                  tab === t
                    ? 'border-brand-600 text-brand-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t === 'employees'
                  ? `Employees (${employees.length})`
                  : t === 'grants'
                  ? `Property Access (${grants.filter((g) => !g.revoked_at).length})`
                  : 'Overview'}
              </button>
            );
          })}
        </nav>
      </div>

      {tab === 'overview' && (
        <VendorOverviewTab
          vendor={vendor}
          canEdit={canManageVendor}
          onSaved={(v) => {
            setVendor(v);
            onChanged?.(v);
          }}
        />
      )}

      {tab === 'employees' && (
        <EmployeesTab
          vendorId={vendor.id}
          employees={employees}
          setEmployees={setEmployees}
          canManage={canManageEmployees}
        />
      )}

      {tab === 'grants' && !readonlyView && (
        <GrantsTab
          vendorId={vendor.id}
          employees={employees}
          grants={grants}
          setGrants={setGrants}
          properties={properties}
          canManage={canManageGrants}
          scopedPropertyId={scopedPropertyId}
        />
      )}
    </div>
  );
};

// ─── Overview tab ─────────────────────────────────────────────────────────

const VendorOverviewTab: React.FC<{
  vendor: Vendor;
  canEdit: boolean;
  onSaved: (v: Vendor) => void;
}> = ({ vendor, canEdit, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(vendor.name);
  const [email, setEmail] = useState(vendor.contact_email || '');
  const [phone, setPhone] = useState(vendor.contact_phone || '');
  const [details, setDetails] = useState<Partial<Vendor>>(vendor);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(vendor.name);
    setEmail(vendor.contact_email || '');
    setPhone(vendor.contact_phone || '');
    setDetails(vendor);
  }, [vendor]);

  const detailFields: { key: keyof Vendor; label: string; type?: string }[] = [
    { key: 'category', label: 'Category' },
    { key: 'legal_name', label: 'Legal name' },
    { key: 'website', label: 'Website', type: 'url' },
    { key: 'address_line1', label: 'Address line 1' },
    { key: 'address_line2', label: 'Address line 2' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'postal_code', label: 'Postal code' },
    { key: 'primary_contact_name', label: 'Primary contact' },
    { key: 'emergency_contact_name', label: 'Emergency contact' },
    { key: 'emergency_contact_phone', label: 'Emergency phone', type: 'tel' },
    { key: 'license_number', label: 'License number' },
    { key: 'insurance_expires_at', label: 'Insurance expires', type: 'date' },
    { key: 'logo_url', label: 'Logo URL', type: 'url' },
  ];

  const setDetail = (key: keyof Vendor, value: string) =>
    setDetails((current) => ({ ...current, [key]: value }));

  const save = async () => {
    if (!name.trim()) {
      alert('Vendor name is required.');
      return;
    }
    const patch: Partial<Vendor> = {};
    if (name.trim() !== vendor.name) patch.name = name.trim();
    if (email.trim() !== (vendor.contact_email || '')) patch.contact_email = email.trim();
    if (phone.trim() !== (vendor.contact_phone || '')) patch.contact_phone = phone.trim();
    for (const { key } of detailFields) {
      const value = String(details[key] ?? '').trim();
      if (value !== String(vendor[key] ?? '').trim()) Object.assign(patch, { [key]: value });
    }
    if (String(details.notes ?? '').trim() !== (vendor.notes || '').trim()) {
      patch.notes = String(details.notes ?? '').trim();
    }
    if ((details.is_amenity === true) !== (vendor.is_amenity === true)) {
      patch.is_amenity = details.is_amenity === true;
    }
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const updated = await updateVendor(vendor.id, patch);
      onSaved(updated);
      setEditing(false);
    } catch (e: any) {
      alert(`Failed to save: ${e?.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Name</p>
          <p className="font-medium text-gray-900">{vendor.name}</p>
        </div>
        <div>
          <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Contact Email</p>
          <p className="font-medium text-gray-900">{vendor.contact_email || '—'}</p>
        </div>
        <div>
          <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Contact Phone</p>
          <p className="font-medium text-gray-900">{vendor.contact_phone || '—'}</p>
        </div>
        <div>
          <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Created</p>
          <p className="font-medium text-gray-900">
            {vendor.created_at ? formatDate(vendor.created_at) : '—'}
          </p>
        </div>
        <div>
          <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Status</p>
          <p className="font-medium text-gray-900">
            {vendor.revoked_at ? 'Revoked' : 'Active'}
          </p>
        </div>
        {detailFields.map(({ key, label }) => (
          <div key={key}>
            <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">{label}</p>
            <p className="font-medium text-gray-900 break-words">{String(vendor[key] ?? '') || '—'}</p>
          </div>
        ))}
        <div>
          <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Amenity</p>
          <p className="font-medium text-gray-900">{vendor.is_amenity ? 'Yes' : 'No'}</p>
        </div>
        <div className="sm:col-span-2">
          <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Notes</p>
          <p className="font-medium text-gray-900 whitespace-pre-wrap break-words">{vendor.notes || '—'}</p>
        </div>
        {canEdit && (
          <div className="sm:col-span-2 pt-2">
            <Button variant="secondary" onClick={() => {
              setName(vendor.name);
              setEmail(vendor.contact_email || '');
              setPhone(vendor.contact_phone || '');
              setDetails(vendor);
              setEditing(true);
            }}>
              Edit
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3 text-sm">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Contact Email</label>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Contact Phone</label>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {detailFields.map(({ key, label, type }) => (
          <div key={key}>
            <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor={`vendor-${key}`}>{label}</label>
            <input
              id={`vendor-${key}`}
              type={type || 'text'}
              value={type === 'date' ? String(details[key] ?? '').slice(0, 10) : String(details[key] ?? '')}
              onChange={(e) => setDetail(key, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        ))}
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={details.is_amenity === true}
          onChange={(e) => setDetails((current) => ({ ...current, is_amenity: e.target.checked }))} />
        Amenity vendor
      </label>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="vendor-notes">Notes</label>
        <textarea id="vendor-notes" value={String(details.notes ?? '')}
          onChange={(e) => setDetail('notes', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={3} />
      </div>
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={() => setEditing(false)}
          className="px-3 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
        >
          Cancel
        </button>
        <Button onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

// ─── Employees tab ────────────────────────────────────────────────────────

const EmployeesTab: React.FC<{
  vendorId: string;
  employees: VendorEmployee[];
  setEmployees: React.Dispatch<React.SetStateAction<VendorEmployee[]>>;
  canManage: boolean;
}> = ({ vendorId, employees, setEmployees, canManage }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const activate = async (emp: VendorEmployee) => {
    if (!window.confirm(
      `Send activation code to ${emp.first_name} ${emp.last_name} at ${emp.phone || emp.email}?`
    )) return;
    setBusyId(emp.id);
    setBanner(null);
    try {
      const r = await activateVendorEmployee(vendorId, emp.id, { channel: 'both' });
      const sentTo = r.sent_to?.join(', ') || emp.phone || emp.email;
      setBanner(
        `Activation sent to ${sentTo}. Employee will receive a 6-digit code + install link.`
      );
      // Refresh emp row
      setEmployees((prev) =>
        prev.map((e) =>
          e.id === emp.id
            ? { ...e, activation_code: r.activation_code ?? '****' }
            : e
        )
      );
    } catch (e: any) {
      alert(`Activation failed: ${e?.message || e}`);
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (e: any) => (
        <div>
          <div className="font-medium text-gray-900">
            {e.first_name} {e.last_name}
          </div>
          {e.role === 'vendor_admin' && (
            <span className="inline-block mt-0.5 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-100">
              Admin
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      render: (e: any) => (
        <div className="text-xs">
          <div className="text-gray-700">{e.email || '—'}</div>
          <div className="text-gray-500">{e.phone || '—'}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (e: any) =>
        e.revoked_at ? (
          <Badge status="suspended">Revoked</Badge>
        ) : e.activated_at ? (
          <Badge status="active">Activated</Badge>
        ) : e.activation_code ? (
          <Badge status="pending">Code Sent</Badge>
        ) : (
          <Badge status="pending">Pending</Badge>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (e: any) => {
        if (!canManage) return null;
        if (e.revoked_at) return null;
        if (e.activated_at) return <span className="text-xs text-gray-400">—</span>;
        return (
          <button
            onClick={() => activate(e)}
            disabled={busyId === e.id}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-brand-100 text-brand-700 hover:bg-brand-200 disabled:opacity-50"
          >
            {busyId === e.id ? 'Sending…' : 'Send Activation'}
          </button>
        );
      },
    },
  ];

  return (
    <div className="space-y-3">
      {banner && (
        <div className="bg-green-50 border border-green-200 rounded p-2 text-sm text-green-800">
          {banner}
        </div>
      )}
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          Vendor employees receive a 6-digit activation code + mobile install link over SMS + email.
          They log in as person_type = <code>vendor</code> and see only their own record.
        </p>
        {canManage && (
          <Button variant="secondary" onClick={() => setShowAdd(true)}>
            + Add Employee
          </Button>
        )}
      </div>

      <Table columns={columns} data={employees} emptyMessage="No employees yet" />

      {showAdd && (
        <AddEmployeeModal
          vendorId={vendorId}
          onClose={() => setShowAdd(false)}
          onAdded={(e) => {
            setEmployees((prev) => [e, ...prev]);
            setShowAdd(false);
          }}
        />
      )}
    </div>
  );
};

const AddEmployeeModal: React.FC<{
  vendorId: string;
  onClose: () => void;
  onAdded: (e: VendorEmployee) => void;
}> = ({ vendorId, onClose, onAdded }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<'vendor' | 'vendor_admin'>('vendor');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setErr('First and last name required');
      return;
    }
    if (!email.trim() && !phone.trim()) {
      setErr('Email or phone required to send activation');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const created = await createVendorEmployee(vendorId, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        role,
      });
      onAdded(created);
    } catch (e: any) {
      setErr(e?.message || 'Failed to add employee');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl max-w-md w-full mx-4 p-5 space-y-3">
        <h3 className="text-lg font-semibold text-gray-900">Add Employee</h3>
        {err && (
          <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">
            {err}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">First Name *</label>
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Last Name *</label>
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as any)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
          >
            <option value="vendor">Vendor Employee</option>
            <option value="vendor_admin">Vendor Admin (can manage other employees)</option>
          </select>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            Cancel
          </button>
          <Button onClick={submit} disabled={saving}>
            {saving ? 'Adding…' : 'Add Employee'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Grants tab ───────────────────────────────────────────────────────────

const GrantsTab: React.FC<{
  vendorId: string;
  employees: VendorEmployee[];
  grants: VendorGrant[];
  setGrants: React.Dispatch<React.SetStateAction<VendorGrant[]>>;
  properties: Record<string, any>;
  canManage: boolean;
  scopedPropertyId?: string;
}> = ({ vendorId, employees, grants, setGrants, properties, canManage, scopedPropertyId }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const filtered = useMemo(
    () =>
      scopedPropertyId
        ? grants.filter((g) => String(g.property_id) === String(scopedPropertyId))
        : grants,
    [grants, scopedPropertyId]
  );

  const revoke = async (g: VendorGrant) => {
    const employee = employees.find((row) => row.id === g.employee_id);
    const subject = employee ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim() : 'all vendor employees';
    if (!window.confirm(`Revoke this property grant for ${subject}? Access is removed immediately.`)) return;
    setBusyId(g.id);
    try {
      const updated = await revokeVendorGrant(vendorId, g.id);
      setGrants((prev) => prev.map((row) => (row.id === g.id ? updated : row)));
    } catch (e: any) {
      alert(`Revoke failed: ${e?.message || e}`);
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    {
      key: 'employee',
      header: 'Employee',
      render: (g: VendorGrant) => {
        const employee = employees.find((row) => row.id === g.employee_id);
        return employee
          ? [employee.first_name, employee.last_name].filter(Boolean).join(' ') || employee.email || 'Employee'
          : 'All employees';
      },
    },
    {
      key: 'property',
      header: 'Property',
      render: (g: any) => {
        const p = properties[String(g.property_id)];
        return (
          <div>
            <div className="font-medium text-gray-900">
              {p?.name || `#${g.property_id}`}
            </div>
            {p?.address && <div className="text-xs text-gray-500">{p.address}</div>}
          </div>
        );
      },
    },
    {
      key: 'type',
      header: 'Type',
      render: (g: any) =>
        g.access_type === 'short_term' ? (
          <Badge status="pending">Short-term</Badge>
        ) : (
          <Badge status="active">Long-term</Badge>
        ),
    },
    {
      key: 'window',
      header: 'Valid',
      render: (g: any) => (
        <div className="text-xs">
          <div>{formatDate(g.valid_from)}</div>
          {g.valid_to && <div className="text-gray-500">→ {formatDate(g.valid_to)}</div>}
          {!g.valid_to && <div className="text-gray-400">No expiry</div>}
        </div>
      ),
    },
    {
      key: 'methods',
      header: 'Methods',
      render: (g: any) => (
        <div className="text-xs text-gray-700">
          {(g.allowed_methods || []).join(', ') || '—'}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (g: any) =>
        g.revoked_at ? <Badge status="suspended">Revoked</Badge> : <Badge status="active">Active</Badge>,
    },
    {
      key: 'actions',
      header: '',
      render: (g: any) =>
        canManage && !g.revoked_at ? (
          <button
            onClick={() => revoke(g)}
            disabled={busyId === g.id}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
          >
            {busyId === g.id ? 'Revoking…' : 'Revoke'}
          </button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          Grants define which properties this vendor can access and how (PIN, card, face, mobile app,
          relay button). BLE is disabled for vendors by policy.
        </p>
        {canManage && (
          <Button variant="secondary" onClick={() => setShowAdd(true)}>
            + Grant Property Access
          </Button>
        )}
      </div>

      <Table columns={columns} data={filtered} emptyMessage="No property grants yet" />

      {showAdd && (
        <AddGrantModal
          vendorId={vendorId}
          employees={employees}
          properties={properties}
          defaultPropertyId={scopedPropertyId}
          onClose={() => setShowAdd(false)}
          onAdded={(g) => {
            setGrants((prev) => [g, ...prev]);
          }}
        />
      )}
    </div>
  );
};

const AddGrantModal: React.FC<{
  vendorId: string;
  employees: VendorEmployee[];
  properties: Record<string, any>;
  defaultPropertyId?: string;
  onClose: () => void;
  onAdded: (g: VendorGrant) => void;
}> = ({ vendorId, employees, properties, defaultPropertyId, onClose, onAdded }) => {
  const [employeeId, setEmployeeId] = useState('');
  const [propertyId, setPropertyId] = useState(defaultPropertyId || '');
  const [accessType, setAccessType] = useState<'short_term' | 'long_term'>('short_term');
  const [validFrom, setValidFrom] = useState(new Date().toISOString().slice(0, 10));
  const [validTo, setValidTo] = useState('');
  const [methods, setMethods] = useState<string[]>(['pin', 'mobile_app']);
  const [issuanceMode, setIssuanceMode] = useState<'visitor_pass' | 'mobile_app' | 'both'>('both');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [issued, setIssued] = useState<any | null>(null);

  const toggleMethod = (m: string) => {
    setMethods((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  };

  const submit = async () => {
    if (!propertyId) {
      setErr('Select a property');
      return;
    }
    if (!employeeId && methods.length === 0) {
      setErr('Select at least one access method');
      return;
    }
    const employee = employees.find((row) => row.id === employeeId);
    const issuePass = issuanceMode === 'visitor_pass' || issuanceMode === 'both';
    const issueMobile = issuanceMode === 'mobile_app' || issuanceMode === 'both';
    if (employeeId && issuePass && !employee?.phone) {
      setErr('This employee needs a phone number before a secure VisitorPass can be issued.');
      return;
    }
    if (employeeId && issuePass && !validTo) {
      setErr('Set a Valid Until date when VisitorPass access is required.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const created = await createVendorGrant(vendorId, {
        employee_id: employeeId || null,
        property_id: propertyId,
        access_type: accessType,
        valid_from: new Date(validFrom).toISOString(),
        valid_to: validTo ? new Date(validTo).toISOString() : null,
        allowed_methods: Array.from(new Set([
          ...methods.filter((method) => method !== 'pin' && method !== 'mobile_app'),
          ...(issuePass ? ['pin'] : []),
          ...(issueMobile ? ['mobile_app'] : []),
        ])),
        device_ids: [],
        notes: notes.trim() || null,
      });
      onAdded(created);
      if (employee) {
        let pass: any = null;
        let activation: any = null;
        if (issuePass) {
          const end = new Date(validTo);
          end.setHours(23, 59, 59, 999);
          pass = await apiPost('/visitors/passes', {
            property_id: propertyId,
            visitor_name: [employee.first_name, employee.last_name].filter(Boolean).join(' ') || 'Vendor employee',
            phone: employee.phone,
            email: employee.email || null,
            pass_type: 'vendor',
            valid_from: new Date(validFrom).toISOString(),
            valid_until: end.toISOString(),
            access_scope: [],
            delivery_instructions: notes.trim() || `Vendor access for ${employee.job_title || 'employee'}`,
          });
        }
        if (issueMobile) {
          activation = await activateVendorEmployee(vendorId, employee.id, { property_id: propertyId });
        }
        setIssued({ kind: 'employee', pass, activation });
      } else {
        setIssued({ kind: 'grant' });
      }
    } catch (e: any) {
      setErr(e?.message || 'Failed to create grant');
    } finally {
      setSaving(false);
    }
  };

  const propertyOptions = Object.values(properties);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl max-w-lg w-full mx-4 p-5 space-y-3">
        {issued ? (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Access Ready</h3>
            {issued.pass && <>
              <p className="text-sm text-gray-600">The employee-specific secure VisitorPass and directory code were generated.</p>
              <div className="border border-gray-300 rounded-lg p-3"><div className="text-xs text-gray-500">Directory code</div><div className="font-mono text-2xl font-bold">{issued.pass.directory_code}</div></div>
              <input readOnly value={issued.pass.pass_url || ''} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <Button onClick={() => navigator.clipboard.writeText(issued.pass.pass_url || '')}>Copy secure link</Button>
            </>}
            {issued.activation && <>
              <p className="text-sm text-gray-600">Long-term mobile app activation was generated for this employee.</p>
              <div className="border border-gray-300 rounded-lg p-3"><div className="text-xs text-gray-500">Activation code</div><div className="font-mono text-2xl font-bold">{issued.activation.activation_code || 'Sent to employee'}</div></div>
            </>}
            {issued.kind === 'grant' && <p className="text-sm text-gray-600">The property grant now applies to all active vendor employees.</p>}
            <div className="flex justify-end"><Button onClick={onClose}>Done</Button></div>
          </div>
        ) : <>
        <h3 className="text-lg font-semibold text-gray-900">Grant Property Access</h3>
        {err && (
          <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">
            {err}
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Employee *</label>
          <select
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
          >
            <option value="">All vendor employees</option>
            {employees.map((employee) => (
              <option key={employee.id} value={employee.id}>
                {[employee.first_name, employee.last_name].filter(Boolean).join(' ') || employee.email || employee.id}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-gray-500 mt-1">Choose one employee for individual access, or explicitly grant the entire vendor team.</p>
        </div>
        {employeeId && <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Required access experience *</label>
          <select value={issuanceMode} onChange={(e) => setIssuanceMode(e.target.value as any)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
            <option value="visitor_pass">VisitorPass only</option>
            <option value="mobile_app">Mobile app only</option>
            <option value="both">VisitorPass and mobile app</option>
          </select>
          <p className="text-[11px] text-gray-500 mt-1">The property manager controls whether this employee may use one experience or both.</p>
        </div>}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Property *</label>
          <select
            value={propertyId}
            onChange={(e) => setPropertyId(e.target.value)}
            disabled={!!defaultPropertyId}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
          >
            <option value="">— Select —</option>
            {propertyOptions.map((p: any) => (
              <option key={p.uuid || p.id} value={String(p.uuid || p.id)}>
                {p.name || `Property #${p.id}`}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Access Type</label>
            <select
              value={accessType}
              onChange={(e) => setAccessType(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              <option value="short_term">Short-term (visit)</option>
              <option value="long_term">Long-term (recurring)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Valid From</label>
            <input
              type="date"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Valid Until <span className="text-gray-400">(optional)</span>
          </label>
          <input
            type="date"
            value={validTo}
            onChange={(e) => setValidTo(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Allowed Methods</label>
          <div className="flex flex-wrap gap-2">
            {METHOD_OPTIONS.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => toggleMethod(m)}
                className={`px-3 py-1.5 text-xs rounded-lg border ${
                  methods.includes(m)
                    ? 'bg-brand-600 text-white border-brand-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {m.replace('_', ' ')}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-gray-400 mt-1">BLE is disabled for vendors by policy.</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            Cancel
          </button>
          <Button onClick={submit} disabled={saving}>
            {saving ? 'Granting…' : 'Grant Access'}
          </Button>
        </div>
        </>}
      </div>
    </div>
  );
};

export default VendorDetail;
